import {
    Table,
    TableBody,
    TableContainer,
    TableRow,
    Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { dateTimeConvertor } from "src/utils/helperFunctions";
import EmptyTable from "../table/EmptyTable";
import useData from "src/hooks/useApi";
import {
    HeadingType,
    OrderPropsType,
    TableData,
    data,
} from "src/types/submissionsTypes";
import SubmissionsHeadRow from "./SubmissionsPageHeadRow";
import PopupButton from "../common/PopupButton";
import useMessage from "src/hooks/useMessage";
import StatusChip from "../common/SubmissionStatusChip";
import { DateTime } from "luxon";
import LoadingTable from "../table/LoadingTable";
import { StyledTableCell } from "../SharedStyles";

const headRow: HeadingType[] = [
    {
        id: "domain",
        label: "Domain",
        align: "left",
        sorting: true,
    },
    {
        id: "date",
        label: "Added Time (UTC)",
        align: "center",
        sorting: true,
    },
    {
        id: "status",
        label: "Status",
        align: "center",
        sorting: true,
    },
    {
        id: "message",
        label: "Message",
        align: "center",
        sorting: true,
    },
    {
        id: "id",
        label: "Actions",
        align: "center",
    },
];

const StyledTableRow = styled(TableRow)(() => ({
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

type SubmissionTableProps = {
    apiData: data[];
    orderProps: OrderPropsType;
    setOrderProps: Dispatch<SetStateAction<OrderPropsType>>;
    setResubmitTrigger: Dispatch<SetStateAction<boolean>>;
    setEndDate: Dispatch<SetStateAction<DateTime<true>>>;
    loading?: boolean;
};

const SubmissionsTable = (props: SubmissionTableProps) => {
    const {
        apiData,
        orderProps,
        setOrderProps,
        setResubmitTrigger,
        setEndDate,
        loading,
    } = props;

    const [rows, setRows] = useState<TableData[]>([]);
    const { postData } = useData();
    const { showSuccess, showError } = useMessage();

    async function resubmitBid(row: TableData) {
        try {
            const response = await postData("/bid/retryFailedSubmission", {
                submissionId: row.id,
            });

            if (!response) {
                throw new Error("Retry Response not received");
            }

            if (response?.success) {
                showSuccess("Submission Successfully Resubmitted !");
                return;
            }

            if (!response?.success && response?.message) {
                showError(response.message);
                return;
            }

            throw new Error("Error in Retry Response");
        } catch {
            showError("Error, please retry adding the bid");
        } finally {
            setEndDate(DateTime.now().plus({ hours: 1 }));
            setResubmitTrigger((resubmitTrigger) => !resubmitTrigger);
        }
    }

    useEffect(() => {
        const convertedData: TableData[] = apiData.map((item) => ({
            ...item,
            date: item.date
                ? `${dateTimeConvertor(+item.date).date} ${
                      dateTimeConvertor(+item.date).time
                  }`
                : "",
        }));
        setRows(convertedData);
    }, [apiData]);

    return (
        <TableContainer
            sx={{
                display: "flex",
                flexDirection: "column",
                width: { md: "100%" },
                minWidth: { sm: "500px" },
            }}
        >
            <Table
                sx={{
                    width: "100%",
                    mx: "auto",
                }}
            >
                <SubmissionsHeadRow
                    order={orderProps.order}
                    orderBy={orderProps.orderBy}
                    headRow={headRow}
                    setOrderProps={setOrderProps}
                />
                <TableBody>
                    {loading ? (
                        <LoadingTable colspan={headRow.length} />
                    ) : apiData.length > 0 ? (
                        rows.map((row) => (
                            <StyledTableRow key={row.id}>
                                <StyledTableCell
                                    align="left"
                                    component="th"
                                    scope="row"
                                >
                                    {row.domain}
                                </StyledTableCell>
                                <StyledTableCell
                                    align="center"
                                    component="th"
                                    scope="row"
                                >
                                    {row.date}
                                </StyledTableCell>
                                <StyledTableCell align="center">
                                    <StatusChip type={row.status} />
                                </StyledTableCell>
                                <StyledTableCell
                                    align="center"
                                    component="th"
                                    scope="row"
                                >
                                    {row.message}
                                </StyledTableCell>
                                <StyledTableCell
                                    align="center"
                                    component="th"
                                    scope="row"
                                    sx={{
                                        my: 1,
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            !row.isResubmitAllowed &&
                                            row.attemptCount > 3
                                                ? "Maximum attempt count reached"
                                                : ""
                                        }
                                        placement="top"
                                    >
                                        <span>
                                            <PopupButton
                                                type="proceed"
                                                id="resubmit-button"
                                                triggerFunction={() => {
                                                    resubmitBid(row);
                                                }}
                                                height="35px"
                                                styleProps={{
                                                    fontSize: 12,
                                                    px: { md: 2, xs: 1 },
                                                    fontWeight: 500,
                                                    "&&:disabled": {
                                                        bgcolor: "#D8D7D9",
                                                    },
                                                }}
                                                disabled={
                                                    !row.isResubmitAllowed
                                                }
                                            >
                                                Resubmit
                                            </PopupButton>
                                        </span>
                                    </Tooltip>
                                </StyledTableCell>
                            </StyledTableRow>
                        ))
                    ) : (
                        <EmptyTable
                            msg="No submissions"
                            colSpan={headRow.length}
                        />
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default SubmissionsTable;
